import { createAction, createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import keyBy from "lodash/keyBy";
import * as types from "./types";
import { toggleSmartMatch } from "../../cargo";
import { getAllArchivedRoutes } from "../../routes";
import { AppThunkAction } from "../../../redux-store";
import { routeViewModelsDecoded } from "../route-views";
import * as authSelectors from "../../auth/selectors";
import { debouncedLoadCalendarViewData, loadCalendarViewData } from "../calendar-view";
import { splitCargo, unsplitCargo } from "../split-cargo";
import { openCargoFoundDeleted } from "../../app/cargo-dialog";

const prefix = "data/cargo-views";

type State = {
  entities: Record<string, types.CargoView>;
};

const initialState: State = {
  entities: {},
};

// One action per batch of socket `cargo-view.updated` / `cargo-view.deleted`
// messages, not one per message (DORA-385). See SocketConnection.
export const cargoViewsDecoded = createAction<{
  cargoViews: types.CargoView[];
  myTeams: string[];
}>(`${prefix}/cargo-views-decoded`);

export const cargoViewsUpdated =
  (views: unknown[]): AppThunkAction =>
  (dispatch, getState) => {
    const cargoViews: types.CargoView[] = [];
    let invalid = 0;
    for (const view of views) {
      const result = types.cargoViewT.decode(view);
      if (result._tag === "Right") {
        cargoViews.push(result.right);
      } else {
        invalid++;
      }
    }
    if (cargoViews.length > 0) {
      const myTeams = authSelectors.selectMyTeamsIds(getState());
      dispatch(cargoViewsDecoded({ cargoViews, myTeams }));
      if (getState().data.calendarView.pageInView) {
        debouncedLoadCalendarViewData(dispatch);
      }
    }
    // Apply the valid ones first, so one bad message doesn't drop the batch.
    if (invalid > 0) {
      throw new Error("Decode error");
    }
  };

export const cargoViewUpdated = (view: unknown): AppThunkAction =>
  cargoViewsUpdated([view]);

export const loadUndispatchedCargoViews = createErrorReportingAsyncThunk(
  `${prefix}/load-undispatched-cargos`,
  async () =>
    axios
      .get("/api/cargo-views/undispatched")
      .then(getData)
      .then(tPromise.decode(types.cargoViewsT))
);

export const loadCargoViewsForRoute = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-views-for-route`,
  async (routeId: string) =>
    axios
      .get(`/api/cargo-views/for-route/${routeId}`)
      .then(getData)
      .then(tPromise.decode(types.cargoViewsT))
);

export const loadCargoViewsForRoutes = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-views-for-routes`,
  async (routesIds: string[]) =>
    axios
      .post(`/api/cargo-views/for-routes`, { routesIds })
      .then(getData)
      .then(tPromise.decode(types.cargoViewsT))
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(routeViewModelsDecoded, (state, action) => {
        // Assign per cargo rather than spreading into a new entities object,
        // so a route update that carries no cargos leaves cargo views (and
        // every selector reading them) untouched (DORA-385).
        for (const { cargos } of action.payload.models) {
          for (const cargo of cargos) {
            state.entities[cargo.id] = cargo;
          }
        }
      })
      .addCase(cargoViewsDecoded, (state, action) => {
        for (const cargoView of action.payload.cargoViews) {
          state.entities[cargoView.id] = cargoView;
        }
      })
      .addCase(openCargoFoundDeleted, (state, action) => {
        const cargoView = state.entities[action.payload.cargoId];
        if (cargoView) {
          cargoView.deleted = true;
        }
      })
      .addCase(loadUndispatchedCargoViews.fulfilled, (state, action) => {
        state.entities = {
          ...state.entities,
          ...keyBy(action.payload, "id"),
        };
      })
      .addCase(loadCargoViewsForRoute.fulfilled, (state, action) => {
        state.entities = {
          ...state.entities,
          ...keyBy(action.payload, "id"),
        };
      })
      .addCase(loadCargoViewsForRoutes.fulfilled, (state, action) => {
        state.entities = {
          ...state.entities,
          ...keyBy(action.payload, "id"),
        };
      })
      .addCase(getAllArchivedRoutes.fulfilled, (state, action) => {
        // Merge (don't replace) so the archived search doesn't wipe other cargo
        // views from the store — notably the undispatched cargos on the left.
        state.entities = {
          ...state.entities,
          ...keyBy(action.payload.cargoViews, "id"),
        };
      })
      .addCase(toggleSmartMatch.fulfilled, (state, action) => {
        const arg = action.meta.arg;
        const cargo = state.entities[arg];
        if (cargo) {
          cargo.smartMatch = !cargo.smartMatch;
        }
      })
      .addCase(splitCargo.fulfilled, (state, action) => {
        const { cargoId } = action.meta.arg;
        if (state.entities[cargoId]) {
          state.entities[cargoId].hasBeenSplit = true;
        }
      })
      .addCase(unsplitCargo.fulfilled, (state, action) => {
        const id = action.meta.arg;
        if (state.entities[id]) {
          state.entities[id].hasBeenSplit = false;
        }
      });
  },
});

export default slice.reducer;
